<script lang="ts">
	/**
	 * The provider's webhooks, as this store received them (Design §6, §13).
	 *
	 * A `failed` event is a charge, a cancellation or a refund the provider has already applied on
	 * its side and this store has not. Nothing downstream retries it forever, so the queue is the
	 * only place the divergence is visible and Retry is the only thing that closes it.
	 *
	 * The payload is deliberately absent. It carries the raw provider object — card metadata,
	 * addresses, amounts — and reading one is a securityAdmin action that is audited per view, so
	 * it does not belong on a screen an operator leaves open all day. The table says so rather than
	 * leaving the omission to be discovered.
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, InfiniteLoadElement, Panel, RelativeDate, SelectField } from '../../../components';
	import { listProviderEvents, retryProviderEvent } from '../../../AppNetwork';
	import type { ProviderEvent } from '../../../AppTypes';
	import { toastError, toastSuccess } from '../../../stores/toast';

	const PAGE_SIZE = 25;

	let events: ProviderEvent[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadError: string = '';
	let busyUUID: string = '';
	let statusFilter: string = '';
	let sentinel: InfiniteLoadElement | undefined;

	const statusOptions = [
		{ value: '', name: 'Any status', description: 'No narrowing by status.' },
		{ value: 'received', name: 'Received', description: 'Stored from the provider’s webhook and waiting for the event worker.' },
		{ value: 'processing', name: 'Processing', description: 'Claimed by the event worker right now.' },
		{ value: 'processed', name: 'Processed', description: 'Applied to the store’s records.' },
		{ value: 'failed', name: 'Failed', description: 'Processing gave up after its retries; needs an admin to look and retry.' },
		{ value: 'ignored', name: 'Ignored', description: 'A type the store does not act on, or about something it does not know. Kept for reference.' }
	];

	const statusColors: Record<string, 'green' | 'blue' | 'yellow' | 'red' | 'dark'> = {
		received: 'dark',
		processing: 'blue',
		processed: 'green',
		failed: 'red',
		ignored: 'yellow'
	};

	async function fnLoad(reset: boolean = true): Promise<void> {
		if (reset) {
			loading = true;
			events = [];
		}
		loadError = '';
		try {
			const page = await listProviderEvents({
				status: statusFilter,
				limit: PAGE_SIZE,
				offset: reset ? 0 : events.length
			});
			events = reset ? page.items : [...events, ...page.items];
			total = page.total;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnRetry(event: ProviderEvent): Promise<void> {
		busyUUID = event.uuid;
		try {
			await retryProviderEvent(event.uuid);
			toastSuccess('Queued for reprocessing.');
			await fnLoad();
		} catch (error) {
			toastError('The event was not queued.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnLoadMore(): Promise<void> {
		await fnLoad(false);
		sentinel?.resetVisibilityTracking();
		sentinel?.updateState();
	}

	$: hasMore = events.length < total;

	onMount(() => fnLoad());
</script>

<Panel title="Provider events" faIcon="satellite-dish">
	<svelte:fragment slot="titleRight">
		<span class="text-default text-xs">{total} {total === 1 ? 'event' : 'events'}</span>
	</svelte:fragment>

	<div class="space-y-4">
		<p class="text-default text-sm">
			A <strong>failed</strong> event is something the payment provider has already done (a charge,
			a cancellation, a refund) that this store has not applied. Retrying is what closes the gap.
			Payloads are not shown here: they hold raw provider data and are read through an audited,
			securityAdmin-only path instead.
		</p>

		<div class="controlpanel flex flex-wrap items-center gap-3">
			<SelectField
				items={statusOptions}
				bind:value={statusFilter}
				on:change={() => fnLoad()}
				placeholder="Status"
				size="sm"
				class="w-44"
			/>
			<Button
				size="sm"
				color={statusFilter === 'failed' ? 'primary' : 'alternative'}
				on:click={() => {
					statusFilter = statusFilter === 'failed' ? '' : 'failed';
					fnLoad();
				}}
			>
				<i class="fa-solid fa-triangle-exclamation mr-2" />Failed only
			</Button>
			<Button size="sm" color="alternative" on:click={() => fnLoad()}>
				<i class="fa-solid fa-arrows-rotate mr-2" />Refresh
			</Button>
		</div>

		{#if loading}
			<EmptyState variant="loading" title="Loading provider events…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="Provider events could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
			</EmptyState>
		{:else if events.length === 0}
			<EmptyState title="No event matches." description="Nothing has arrived from the provider for these filters." />
		{:else}
			<div class="relative overflow-x-auto">
				<Table>
					<TableHead>
						<TableHeadCell>Event</TableHeadCell>
						<TableHeadCell>Occurred</TableHeadCell>
						<TableHeadCell>Status</TableHeadCell>
						<TableHeadCell>Attempts</TableHeadCell>
						<TableHeadCell>Next attempt</TableHeadCell>
						<TableHeadCell>Error</TableHeadCell>
						<TableHeadCell />
					</TableHead>
					<TableBody>
						{#each events as event (event.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<div class="font-medium">{event.type}</div>
									<div class="text-default text-xs font-mono break-all">{event.providerEventID}</div>
								</TableBodyCell>
								<TableBodyCell class="dataTable"><RelativeDate date={event.occurredAt} /></TableBodyCell>
								<TableBodyCell class="dataTable">
									<Badge color={statusColors[event.status] ?? 'dark'}>{event.status}</Badge>
								</TableBodyCell>
								<TableBodyCell class="dataTable">{event.attemptCount}</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if event.processedAt}
										<RelativeDate date={event.processedAt} />
									{:else if event.nextAttemptAt}
										<RelativeDate date={event.nextAttemptAt} />
									{:else}
										<span class="text-default">—</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if event.error}
										<span class="text-default text-xs break-all">{event.error}</span>
									{:else}
										<span class="text-default">—</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if event.status !== 'processed'}
										<Button
											size="xs"
											color="alternative"
											disabled={busyUUID === event.uuid}
											on:click={() => fnRetry(event)}
										>
											Retry event
										</Button>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			</div>

			{#if hasMore}
				<InfiniteLoadElement bind:this={sentinel} on:becomeVisible={fnLoadMore} />
			{/if}
		{/if}
	</div>
</Panel>
